// ── Assembly order statuses ─────────────────────────────────────────────────
//
// The authority is a CHECK constraint on assembly_orders.status:
//
//   CHECK (status = ANY (ARRAY['pending','building','software','testing',
//                              'awaiting_review','completed','Posted','Cancelled']))
//
// It is validated (pg_constraint.convalidated = true), so every existing row
// already conforms and nothing outside this list can be written.
//
// That matters, because the application carried three different ideas of which
// statuses count as "work in progress", between them naming three values the
// database will not accept - 'picking', 'in_progress' and 'In Progress'. Those
// were dead branches: no row can hold them, so nothing ever matched. They are
// dropped here rather than carried forward.

/** Every value assembly_orders.status may hold, per the CHECK constraint. */
export const ASSEMBLY_ORDER_STATUSES = [
  'pending',
  'building',
  'software',
  'testing',
  'awaiting_review',
  'completed',
  'Posted',
  'Cancelled',
] as const;

export type AssemblyOrderStatus = (typeof ASSEMBLY_ORDER_STATUSES)[number];

/**
 * The stages an order moves through on the floor, in order.
 *
 * Excludes 'completed', which sits between the pipeline and 'Posted': the work
 * is done but the units have not been posted to stock. Production boards show
 * these five as columns.
 */
export const PIPELINE_STATUSES = [
  'pending',
  'building',
  'software',
  'testing',
  'awaiting_review',
] as const;

export type PipelineStatus = (typeof PIPELINE_STATUSES)[number];

/** Terminal states - the order is finished or abandoned. */
export const TERMINAL_STATUSES = ['Posted', 'Cancelled'] as const;

const inList = (list: readonly string[], status: string | null | undefined): boolean =>
  !!status && list.includes(status);

export function isPipelineStatus(status: string | null | undefined): boolean {
  return inList(PIPELINE_STATUSES, status);
}

export function isTerminalOrderStatus(status: string | null | undefined): boolean {
  return inList(TERMINAL_STATUSES, status);
}

/**
 * Counts as work underway.
 *
 * Identical to the pipeline stages. 'completed' is deliberately excluded: those
 * units are neither still being worked on nor yet posted, and every caller has
 * always treated them as neither.
 */
export function isActiveOrderStatus(status: string | null | undefined): boolean {
  return isPipelineStatus(status);
}
